/* eslint-disable max-classes-per-file*/

import { TREE_VIEW_SESSION_VARIABLE_NAME } from '~/constants';
import { FormModel } from '~/models/form-model';

export class ObjectUtils {
	/**
	 * Get the resultant value from either a getter or a value
	 */
	public static getValue(valueOrGetter) {
		return typeof valueOrGetter === 'function'
			? valueOrGetter()
			: valueOrGetter;
	}

	/**
	 * Get <code>property</code> of <code>object</code>, or <code>object</code> if <code>property</code> was empty.
	 * Expands '.' properties.
	 *
	 * If result is null or undefined at any level, returns undefined.
	 */
	public static getProperty(
		object: { [fieldName: string]: any },
		property: string,
	): any | undefined {
		if (!property || object === undefined) {
			return undefined;
		}
		const innerProperties = property.split('.');
		let ret = object;
		for (property of innerProperties) {
			ret = ret[property];
			if (ret === undefined || ret === null) {
				return undefined;
			}
		}
		return ret;
	}

	public static JSONClone(obj) {
		return JSON.parse(JSON.stringify(obj));
	}

	/**
	 * isEmpty will return true if an object has no properties or
	 * is falsey.
	 */
	public static isEmpty(
		obj: { [propertyName: string]: any } | null | undefined,
	): boolean {
		if (!obj) {
			return true;
		}
		for (const key in obj) {
			if (obj.hasOwnProperty(key)) {
				return false;
			}
		}
		return true;
	}

	public static notEmpty(value: string) {
		return (
			value != null &&
			value !== undefined &&
			String(value) !== 'true' &&
			String(value).trim().length !== 0
		);
	}
}

/**
 * Make the resolver for the promise available.
 * Any calls to 'then' will not be processed until after 'trigger' is called.
 */
export class LockUntilReady implements PromiseLike<any> {
	private internalPromise = new Promise(
		(resolver) => (this.triggerOnce = resolver),
	);

	public then<TResult1, TResult2>(
		onfulfilled?: (value: any) => TResult1 | PromiseLike<TResult1>,
		onrejected?: (reason: any) => TResult2 | PromiseLike<TResult2>,
	): PromiseLike<TResult1 | TResult2> {
		return this.internalPromise.then(onfulfilled, onrejected);
	}

	/**
	 * Resolves the promise
	 */
	public triggerOnce: (data?: {
		success: boolean;
		result: FormModel<string>;
	}) => void;
}

export class ElementUtils {
	public static waitUntilVisible(element: Element) {
		return new Promise((resolve, reject) => {
			const isHiddenLoop = () => {
				if (!$(element).is(':hidden')) {
					resolve();
				} else {
					setTimeout(isHiddenLoop, 100);
				}
			};

			isHiddenLoop();
		});
	}
}

/**
 * Utility class for DOM
 */
export class DomUtils {
	/**
	 * check if the mouse event is happening on the element position regardless of the overlay
	 * @param event Mouse event
	 * @param target HTML element to check the position of
	 * @returns true if the mouse position is just above the target
	 */
	public static isClickingOnTheElementPosition(
		event: MouseEvent,
		target: HTMLElement,
	) {
		const clientX = event.clientX;
		const clientY = event.clientY;
		const targetRect = target.getBoundingClientRect();
		return DomUtils.withinClientRect(clientX, clientY, targetRect);
	}
	/**
	 * check the x,y coordinate is within the element rectangle
	 * @param x number of x coordinate
	 * @param y number of y coordinate
	 * @param react element.getBoundingClientRect
	 * @returns true if the (x,y) is within the element rectangle
	 */
	public static withinClientRect(x: number, y: number, react: DOMRect) {
		return (
			x >= react.left &&
			x <= react.right &&
			y >= react.top &&
			y <= react.bottom
		);
	}
}

/**
 * Check if WEBUI for is open in treeview
 * @returns true if in treeview
 */
export function isInTreeView(): boolean {
	return sessionStorage.getItem(TREE_VIEW_SESSION_VARIABLE_NAME) === '1';
}
